import { Search } from '@/DTOS/MentenancePlanning/Search';
import { saveBlobCrossPlatform } from '@/utils/saveBlobCrossPlatform';
import { DownloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, Col, DatePicker, Row, Select, Space, Spin, Table, Tabs, Tag, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import CompanyService from '../../../../services/company/CompanyService';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import MentenancePlanningCalendarTab from '../MentenancePlanningCalendarTab/MentenancePlanningCalendarTab';
import MentenancePlanningMapAssignment from '../MentenancePlanningMapAssignment/MentenancePlanningMapAssignment';

interface MentenanceInterventionOverviewRow {
  groupKey: string;
  companyId?: string;
  companyName: string;
  workPlaceId?: string;
  workPlaceName: string;
  contractId?: string;
  contractNumber?: string;
  contractDateStart?: string;
  contractDateEnd?: string;
  periodicity?: string;
  dateEnd?: string;
  systemTypes: string[];
  lastInterventionFileId?: string;
  lastInterventionFileNumber?: string;
  lastInterventionFileDeliveryDate?: string;
}

interface MentenanceInterventionOverviewClientRow {
  groupKey: string;
  companyId?: string;
  companyName: string;
  totalMentenanceItems?: number;
  items: MentenanceInterventionOverviewRow[];
}

const PERIODS = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];
const PAGE_SIZES = [10, 20, 30, 50];

const sanitizeFilename = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildPdfFilename = (row: MentenanceInterventionOverviewRow) => {
  const number = row.lastInterventionFileNumber ?? 'fisa';
  return `${sanitizeFilename(`${row.companyName} ${row.workPlaceName} ${number}`)}.pdf`;
};

const formatDate = (value?: string) => {
  if (!value) {
    return '-';
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('DD-MM-YYYY') : value;
};

const formatContractPeriod = (contractDateStart?: string, contractDateEnd?: string) => {
  if (!contractDateStart && !contractDateEnd) {
    return '';
  }

  return `${formatDate(contractDateStart)} - ${formatDate(contractDateEnd)}`;
};

const HAS_INTERVENTION_FILE_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'Cu fisa de interventie' },
  { value: false, label: 'Fara fisa de interventie' },
];

interface MentenanceClientNestedTableProps {
  client: MentenanceInterventionOverviewClientRow;
  selectedCompany: string | null;
  selectedWorkPlace: string | null;
  selectedSystemType: string | null;
  selectedPeriodicity: string | null;
  selectedHasInterventionFile: boolean | null;
  selectedDateRange: [Dayjs | null, Dayjs | null];
  onOpenContract: (record: MentenanceInterventionOverviewRow) => void;
  onOpenPreview: (record: MentenanceInterventionOverviewRow) => void;
  onDownload: (record: MentenanceInterventionOverviewRow) => void;
  downloadingInterventionFileId: string | null;
  isPrintingInterventionFile: boolean;
}

const MentenanceClientNestedTable: React.FC<MentenanceClientNestedTableProps> = ({
  client,
  selectedCompany,
  selectedWorkPlace,
  selectedSystemType,
  selectedPeriodicity,
  selectedHasInterventionFile,
  selectedDateRange,
  onOpenContract,
  onOpenPreview,
  onDownload,
  downloadingInterventionFileId,
  isPrintingInterventionFile,
}) => {
  const [nestedSkip, setNestedSkip] = useState<number>(0);
  const [nestedTake, setNestedTake] = useState<number>(10);
  const nestedCurrentPage = Math.floor(nestedSkip / nestedTake) + 1;
  const startDateKey = selectedDateRange[0]?.format('YYYY-MM-DD');
  const endDateKey = selectedDateRange[1]?.format('YYYY-MM-DD');

  useEffect(() => {
    setNestedSkip(0);
    setNestedTake(10);
  }, [
    client?.companyId,
    selectedCompany,
    selectedWorkPlace,
    selectedSystemType,
    selectedPeriodicity,
    selectedHasInterventionFile,
    startDateKey,
    endDateKey,
  ]);

  const nestedSearchPayload: Search = useMemo(
    () => ({
      companyId: client?.companyId || undefined,
      companyName: selectedCompany || undefined,
      workPlaceName: selectedWorkPlace || undefined,
      systemTypeName: selectedSystemType || undefined,
      periodicity: selectedPeriodicity || undefined,
      hasInterventionFile: selectedHasInterventionFile ?? undefined,
      startDate: selectedDateRange[0] ? selectedDateRange[0].format('YYYY-MM-DD') : undefined,
      endDate: selectedDateRange[1] ? selectedDateRange[1].format('YYYY-MM-DD') : undefined,
      skip: nestedSkip,
      take: nestedTake,
    }),
    [
      client?.companyId,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      selectedPeriodicity,
      selectedHasInterventionFile,
      selectedDateRange,
      nestedSkip,
      nestedTake,
    ],
  );

  const { data: nestedResponse, isFetching: isNestedLoading } = useQuery({
    queryKey: [
      'mentenanceInterventionOverviewItems',
      client?.companyId,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      selectedPeriodicity,
      selectedHasInterventionFile,
      startDateKey,
      endDateKey,
      nestedSkip,
      nestedTake,
    ],
    queryFn: () => MentenancePlanningService.searchInterventionOverviewItems(nestedSearchPayload),
    enabled: !!client?.companyId,
    refetchOnWindowFocus: false,
  });

  const nestedItems: MentenanceInterventionOverviewRow[] = nestedResponse?.response ?? [];
  const nestedTotalItems = nestedResponse?.totalItems ?? 0;

  const nestedColumns: any[] = [
    {
      title: 'Punct de lucru',
      dataIndex: 'workPlaceName',
      key: 'workPlaceName',
      sorter: (a: MentenanceInterventionOverviewRow, b: MentenanceInterventionOverviewRow) =>
        (a.workPlaceName || '').localeCompare(b.workPlaceName || '', 'ro', { sensitivity: 'base' }),
      render: (value: string) => value || '-',
    },
    {
      title: 'Nr. Contract',
      dataIndex: 'contractNumber',
      key: 'contractNumber',
      render: (_value: string, record: MentenanceInterventionOverviewRow) => {
        if (!record?.contractId) {
          return '-';
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Tag color='blue'>
              <Button type='link' style={{ paddingInline: 0 }} onClick={() => onOpenContract(record)}>
                {record.contractNumber || 'Deschide contract'}
              </Button>
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Contract data inceput',
      dataIndex: 'contractDateStart',
      key: 'contractDateStart',
      render: (_value: string, record: MentenanceInterventionOverviewRow) => {
        if (!record?.contractId) {
          return '-';
        }

        return <div>{record.contractDateStart ? <span>{record.contractDateStart}</span> : null}</div>;
      },
    },
    {
      title: 'Contract data sfarsit',
      dataIndex: 'contractDateEnd',
      key: 'contractDateEnd',
      render: (_value: string, record: MentenanceInterventionOverviewRow) => {
        if (!record?.contractId) {
          return '-';
        }

        return <div>{record.contractDateEnd ? <span>{record.contractDateEnd}</span> : null}</div>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'systemTypes',
      key: 'systemTypes',
      render: (systemTypes: string[]) =>
        systemTypes?.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {systemTypes.map((systemType) => (
              <Tag key={systemType} color='blue'>
                {systemType}
              </Tag>
            ))}
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'Periodicitate',
      dataIndex: 'periodicity',
      key: 'periodicity',
      sorter: (a: MentenanceInterventionOverviewRow, b: MentenanceInterventionOverviewRow) =>
        (a.periodicity || '').localeCompare(b.periodicity || '', 'ro', { sensitivity: 'base' }),
      render: (value: string) => value || '-',
    },
    {
      title: 'Data final mentenanta',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      sorter: (a: MentenanceInterventionOverviewRow, b: MentenanceInterventionOverviewRow) =>
        dayjs(a.dateEnd || '1900-01-01').valueOf() - dayjs(b.dateEnd || '1900-01-01').valueOf(),
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Numar fisa de interventie',
      dataIndex: 'lastInterventionFileNumber',
      key: 'lastInterventionFileNumber',
      render: (_value: string, record: MentenanceInterventionOverviewRow) =>
        record?.lastInterventionFileId ? (
          <Tag color='blue'>
            {' '}
            <Button type='link' style={{ paddingInline: 0 }} onClick={() => onOpenPreview(record)}>
              {record.lastInterventionFileNumber || 'Vizualizare'}
            </Button>
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'Data livrare fisa',
      dataIndex: 'lastInterventionFileDeliveryDate',
      key: 'lastInterventionFileDeliveryDate',
      render: (value: string, record: MentenanceInterventionOverviewRow) =>
        record?.lastInterventionFileId ? formatDate(value) : '-',
    },
    {
      title: 'Download',
      key: 'download',
      render: (_value: string, record: MentenanceInterventionOverviewRow) =>
        record?.lastInterventionFileId ? (
          <Tooltip title='Descarca PDF'>
            <Button
              icon={<DownloadOutlined />}
              style={{ marginRight: 20 }}
              onClick={() => onDownload(record)}
              loading={downloadingInterventionFileId === record.lastInterventionFileId && isPrintingInterventionFile}
            />
          </Tooltip>
        ) : (
          'Not available'
        ),
    },
  ];

  return (
    <div className='table-scroll-wrapper'>
      <Table
        dataSource={nestedItems}
        columns={nestedColumns}
        rowKey='groupKey'
        loading={isNestedLoading}
        pagination={{
          current: nestedCurrentPage,
          pageSize: nestedTake,
          total: nestedTotalItems,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZES.map((size) => size.toString()),
          showQuickJumper: false,
          onChange: (page: number, pageSize: number) => {
            setNestedSkip((page - 1) * pageSize);
            setNestedTake(pageSize);
          },
          onShowSizeChange: (_current: number, pageSize: number) => {
            setNestedSkip(0);
            setNestedTake(pageSize);
          },
        }}
        locale={{ emptyText: 'Nu exista mentenante pentru acest client.' }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

const MentenanceInterventionOverview: React.FC = () => {
  const { message } = App.useApp();

  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<string | null>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<string | null>(null);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState<string | null>(null);
  const [selectedHasInterventionFile, setSelectedHasInterventionFile] = useState<boolean | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [skip, setSkip] = useState<number>(0);
  const [take, setTake] = useState<number>(10);
  const [downloadingInterventionFileId, setDownloadingInterventionFileId] = useState<string | null>(null);

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['overviewCompanies'],
    queryFn: () =>
      CompanyService.search({
        isWorkPlacesIncluded: true,
        isActive: true,
      }).then((data) => data?.response ?? []),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['overviewSystemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => data?.response ?? []),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!companies?.length) {
      return [];
    }

    if (selectedCompany) {
      const currentCompany = companies.find((company: any) => company?.name === selectedCompany);
      return currentCompany?.workPlaces ?? [];
    }

    const workPlaceMap = new Map<string, any>();
    companies.forEach((company: any) => {
      (company?.workPlaces ?? []).forEach((workPlace: any) => {
        if (workPlace?.name && !workPlaceMap.has(workPlace.name)) {
          workPlaceMap.set(workPlace.name, workPlace);
        }
      });
    });

    return Array.from(workPlaceMap.values());
  }, [companies, selectedCompany]);

  const searchPayload: Search = useMemo(
    () => ({
      companyName: selectedCompany || undefined,
      workPlaceName: selectedWorkPlace || undefined,
      systemTypeName: selectedSystemType || undefined,
      periodicity: selectedPeriodicity || undefined,
      hasInterventionFile: selectedHasInterventionFile ?? undefined,
      startDate: selectedDateRange[0] ? selectedDateRange[0].format('YYYY-MM-DD') : undefined,
      endDate: selectedDateRange[1] ? selectedDateRange[1].format('YYYY-MM-DD') : undefined,
      skip,
      take,
    }),
    [
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      selectedPeriodicity,
      selectedHasInterventionFile,
      selectedDateRange,
      skip,
      take,
    ],
  );

  const {
    data: response,
    isFetching: isOverviewLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'mentenanceInterventionOverview',
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      selectedPeriodicity,
      selectedHasInterventionFile,
      selectedDateRange[0]?.format('YYYY-MM-DD'),
      selectedDateRange[1]?.format('YYYY-MM-DD'),
      skip,
      take,
    ],
    queryFn: () => MentenancePlanningService.searchInterventionOverview(searchPayload),
    refetchOnWindowFocus: false,
  });

  const clientRows: MentenanceInterventionOverviewClientRow[] = response?.response ?? [];
  const totalItems = response?.totalItems ?? 0;
  const totalFilteredMentenanceItems = response?.totalMentenanceItems ?? 0;
  const currentPage = Math.floor(skip / take) + 1;
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const isClientRowExpandable = (record: MentenanceInterventionOverviewClientRow) =>
    (record.totalMentenanceItems ?? 0) > 0;

  const currentPageExpandableRowKeys = useMemo(
    () => clientRows.filter(isClientRowExpandable).map((row) => row.groupKey),
    [clientRows],
  );

  const areAllCurrentPageRowsExpanded = useMemo(
    () =>
      currentPageExpandableRowKeys.length > 0 &&
      currentPageExpandableRowKeys.every((key) => expandedRowKeys.includes(key)),
    [currentPageExpandableRowKeys, expandedRowKeys],
  );

  const { mutate: printInterventionFile, isPending: isPrintingInterventionFile } = useMutation<
    Response,
    Error,
    { record: MentenanceInterventionOverviewRow; action: 'download' | 'newTab'; targetWindow?: Window | null }
  >({
    mutationFn: async ({ record }) => {
      const fileId = record.lastInterventionFileId;
      if (!fileId) {
        throw new Error('ID-ul fisei de interventie lipseste.');
      }

      const response = await InterventionFileService.print(fileId);
      if (!response.ok) {
        throw new Error(`Eroare la incarcarea PDF-ului (${response.status}).`);
      }
      return response;
    },
    onSuccess: async (response, { action, targetWindow, record }) => {
      const blob = await response.blob();
      const filename = buildPdfFilename(record);

      if (action === 'newTab') {
        const blobUrl = window.URL.createObjectURL(blob);
        const escapedFilename = filename.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>PDF - ${escapedFilename}</title></head><body style="margin:0;padding:0;overflow:hidden;"><iframe src="${blobUrl}" style="width:100%;height:100vh;border:none;"></iframe><div style="position:fixed;top:10px;right:10px;z-index:9999;background:rgba(255,255,255,0.95);padding:8px 12px;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.15);"><a href="${blobUrl}" download="${escapedFilename}" style="text-decoration:none;color:#1890ff;font-weight:500;">Descarcă PDF</a></div></body></html>`;
        const htmlBlob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const htmlBlobUrl = window.URL.createObjectURL(htmlBlob);
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = htmlBlobUrl;
        } else {
          window.open(htmlBlobUrl, '_blank', 'noopener,noreferrer');
        }
        setTimeout(() => window.URL.revokeObjectURL(htmlBlobUrl), 60_000);
        return;
      }

      saveBlobCrossPlatform(blob, filename);
    },
    onError: (error: any) => {
      message.error(error?.message ?? 'A aparut o eroare la incarcarea fisierului PDF.');
    },
  });

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    setSelectedWorkPlace(null);
    setSkip(0);
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    setSkip(0);
  };

  const handleDateRangeChange = (value: [Dayjs | null, Dayjs | null] | null) => {
    setSelectedDateRange(value ?? [null, null]);
    setSkip(0);
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    setSkip(0);
  };

  const handlePeriodicityChange = (value: string | null) => {
    setSelectedPeriodicity(value);
    setSkip(0);
  };

  const handleHasInterventionFileChange = (value: boolean | null) => {
    setSelectedHasInterventionFile(value ?? null);
    setSkip(0);
  };

  const handleApplyFilters = () => {
    setSkip(0);
    refetch();
  };

  const handleResetFilters = () => {
    setSelectedCompany(null);
    setSelectedWorkPlace(null);
    setSelectedSystemType(null);
    setSelectedPeriodicity(null);
    setSelectedHasInterventionFile(null);
    setSelectedDateRange([null, null]);
    setSkip(0);
  };

  const handleDownload = (record: MentenanceInterventionOverviewRow) => {
    if (!record?.lastInterventionFileId) {
      return;
    }

    setDownloadingInterventionFileId(record.lastInterventionFileId);
    printInterventionFile(
      { record, action: 'download' },
      {
        onSettled: () => setDownloadingInterventionFileId(null),
      },
    );
  };

  const handleOpenPreview = (record: MentenanceInterventionOverviewRow) => {
    const w = window as any;
    const isApkMode = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function';
    if (isApkMode) {
      handleDownload(record);
      return;
    }
    if (!record?.lastInterventionFileId) {
      return;
    }
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      message.warning('Permiteti ferestrele pop-up pentru a deschide PDF-ul intr-un tab nou.');
      return;
    }
    printInterventionFile({ record, action: 'newTab', targetWindow });
    try {
      targetWindow.opener = null;
    } catch {}
  };

  const handleOpenContract = (record: MentenanceInterventionOverviewRow) => {
    if (!record?.contractId) {
      return;
    }

    window.open(`/service-maintenance/contract/edit?id=${record.contractId}`, '_blank', 'noopener,noreferrer');
  };

  const handleToggleCurrentPageRows = () => {
    setExpandedRowKeys((previous) => {
      const pageKeysSet = new Set(currentPageExpandableRowKeys);
      if (areAllCurrentPageRowsExpanded) {
        return previous.filter((key) => !pageKeysSet.has(key));
      }

      const nextKeysSet = new Set(previous);
      currentPageExpandableRowKeys.forEach((key) => nextKeysSet.add(key));
      return Array.from(nextKeysSet);
    });
  };

  const clientColumns: any[] = [
    {
      title: 'Client',
      dataIndex: 'companyName',
      key: 'companyName',
      sorter: (a: MentenanceInterventionOverviewClientRow, b: MentenanceInterventionOverviewClientRow) =>
        (a.companyName || '').localeCompare(b.companyName || '', 'ro', { sensitivity: 'base' }),
      render: (companyName: string) => <b>{companyName || '-'}</b>,
    },
    {
      title: `Nr. mentenante (Total filtrat: ${totalFilteredMentenanceItems})`,
      key: 'mentenanceCount',
      width: 160,
      render: (_: any, record: MentenanceInterventionOverviewClientRow) => record?.totalMentenanceItems ?? 0,
    },
  ];

  const overviewContent =
    (isCompaniesLoading && !companies?.length) || (isSystemTypesLoading && !systemTypes?.length) ? (
      <Spin className='spinner' size='large' />
    ) : (
      <div className='table-page-no-h-scroll'>
        <Card style={{ padding: '20px', marginBottom: '20px', boxShadow: '0px 4px 10px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginBottom: 20 }}>Vizualizare mentenante si fise de interventie</h2>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder='Filtreaza dupa beneficiar'
                value={selectedCompany}
                onChange={handleCompanyChange}
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp='children'
              >
                {companies?.map((company: any) => (
                  <Select.Option key={company?.id} value={company?.name}>
                    {company?.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder='Filtreaza dupa punct de lucru'
                value={selectedWorkPlace}
                onChange={handleWorkPlaceChange}
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp='children'
              >
                {workPlaces?.map((workPlace: any) => (
                  <Select.Option key={workPlace?.id} value={workPlace?.name}>
                    {workPlace?.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <DatePicker.RangePicker
                placeholder={['Data de la', 'Data pana la']}
                value={selectedDateRange}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder='Filtreaza dupa periodicitate'
                value={selectedPeriodicity}
                onChange={handlePeriodicityChange}
                style={{ width: '100%' }}
                allowClear
              >
                {PERIODS.map((periodicity) => (
                  <Select.Option key={periodicity} value={periodicity}>
                    {periodicity}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder='Filtreaza dupa sistem'
                value={selectedSystemType}
                onChange={handleSystemTypeChange}
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp='children'
              >
                {systemTypes?.map((systemType: any) => (
                  <Select.Option key={systemType?.id} value={systemType?.name}>
                    {systemType?.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder='Fisa de interventie (toate / cu fisa / fara fisa)'
                value={selectedHasInterventionFile}
                onChange={handleHasInterventionFileChange}
                style={{ width: '100%' }}
                allowClear
                options={[
                  { label: 'Toate', value: null },
                  ...HAS_INTERVENTION_FILE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value })),
                ]}
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <Space>
                <Button type='primary' onClick={handleApplyFilters}>
                  Aplica filtre
                </Button>
                <Button onClick={handleResetFilters}>Reseteaza filtre</Button>
              </Space>
            </Col>

            <Col span={24}>
              <Space>
                <Button onClick={handleToggleCurrentPageRows} disabled={currentPageExpandableRowKeys.length === 0}>
                  {areAllCurrentPageRowsExpanded
                    ? 'Restrange toate randurile din pagina'
                    : 'Extinde toate randurile din pagina'}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <div className='table-scroll-wrapper'>
          <Table
            dataSource={clientRows}
            columns={clientColumns}
            rowKey='groupKey'
            loading={isOverviewLoading}
            locale={{ emptyText: 'Nu exista clienti pentru filtrele selectate.' }}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys((keys as string[]) ?? []),
              expandedRowRender: (record: MentenanceInterventionOverviewClientRow) => (
                <MentenanceClientNestedTable
                  client={record}
                  selectedCompany={selectedCompany}
                  selectedWorkPlace={selectedWorkPlace}
                  selectedSystemType={selectedSystemType}
                  selectedPeriodicity={selectedPeriodicity}
                  selectedHasInterventionFile={selectedHasInterventionFile}
                  selectedDateRange={selectedDateRange}
                  onOpenContract={handleOpenContract}
                  onOpenPreview={handleOpenPreview}
                  onDownload={handleDownload}
                  downloadingInterventionFileId={downloadingInterventionFileId}
                  isPrintingInterventionFile={isPrintingInterventionFile}
                />
              ),
              rowExpandable: isClientRowExpandable,
            }}
            pagination={{
              current: currentPage,
              pageSize: take,
              total: totalItems,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZES.map((size) => size.toString()),
              showQuickJumper: false,
              onChange: (page: number, pageSize: number) => {
                setSkip((page - 1) * pageSize);
                setTake(pageSize);
              },
              onShowSizeChange: (_current: number, pageSize: number) => {
                setSkip(0);
                setTake(pageSize);
              },
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>
    );

  return (
    <Tabs
      defaultActiveKey='clientsWorkplacesMap'
      items={[
        {
          key: 'clientsWorkplacesMap',
          label: 'Harta clienti + puncte de lucru',
          children: <MentenancePlanningMapAssignment />,
        },
        {
          key: 'mentenanceCalendar',
          label: 'Calendar mentenante',
          children: <MentenancePlanningCalendarTab />,
        },
        {
          key: 'mentenanceInterventionOverview',
          label: 'Mentenante + Fise interventie',
          children: overviewContent,
        },
      ]}
    />
  );
};

export default MentenanceInterventionOverview;
